// v3 direction routing — pick the recommended track from the 6 screening answers.
//
// Screening scoring is bipolar (agreement with a shadow statement = negative dims).
// We sum dimension points across all routing answers, weighting the "magic button"
// single_choice by its routing_weight (×1.5). The LOWEST-summed dimension is the
// weakest area = the recommended track. Ties are broken by the button's own choice.
import { QuizPage, V3Scores } from '../types/quiz';

export const TRACK_CODES = [
    'cognition',
    'affect',
    'attention',
    'self',
    'motivation',
    'behavior',
    'biophysiology',
    'social',
] as const;

export interface RoutingResult {
    recommendedTrack: string | null;
    ranking: Array<{ code: string; sum: number }>;
    sums: Record<string, number>;
    buttonTrack: string | null; // the dimension the user picked with the magic button
}

const addDims = (target: Record<string, number>, dims: Record<string, number> | undefined, weight = 1): void => {
    if (!dims) return;
    Object.entries(dims).forEach(([k, v]) => {
        if (typeof v === 'number') target[k] = (target[k] || 0) + v * weight;
    });
};

const answered = (v: unknown): boolean => v !== undefined && v !== null && v !== '';

// The dimension a routing single_choice option points at (its most-negative dim).
const optionTrack = (scores?: V3Scores): string | null => {
    if (!scores || !scores.dims) return null;
    let best: string | null = null;
    let bestVal = Infinity;

    Object.entries(scores.dims).forEach(([k, v]) => {
        if (typeof v === 'number' && v < bestVal) {
            bestVal = v;
            best = k;
        }
    });
    return best;
};

export const computeRecommendedTrack = (
    profile: Record<string, any>,
    pages: QuizPage[],
    trackCodes: string[] = [...TRACK_CODES],
): RoutingResult => {
    const sums: Record<string, number> = {};
    let buttonTrack: string | null = null;

    (pages || []).forEach((page) => {
        if (page.type === 'single_choice' && (page as any).routing && Array.isArray((page as any).options)) {
            const field = page.data_field;
            const val = field ? profile[field] : undefined;

            if (!answered(val)) return;
            const opt = (page as any).options.find((o: any) => o.value === val || o.id === val);

            if (!opt) return;
            const weight = (page as any).routing_weight || 1;

            addDims(sums, opt.scores && opt.scores.dims, weight);
            buttonTrack = optionTrack(opt.scores);
        } else if (page.type === 'scale_group' && (page as any).routing && Array.isArray((page as any).statements)) {
            (page as any).statements.forEach((st: any) => {
                const val = profile[st.data_field];

                if (!answered(val)) return;
                const level: V3Scores | undefined = st.scores && st.scores[String(val)];

                addDims(sums, level && level.dims, 1);
            });
        }
    });

    // Rank the known tracks by summed points ascending (most negative first = weakest).
    const ranking = trackCodes.map((code) => ({ code, sum: sums[code] || 0 })).sort((a, b) => a.sum - b.sum);

    let recommendedTrack: string | null = ranking.length ? ranking[0].code : null;
    // Tie at the bottom → prefer the button's own choice if it's among the tied.

    if (ranking.length && buttonTrack) {
        const lowest = ranking[0].sum;
        const tied = ranking.filter((r) => r.sum === lowest).map((r) => r.code);

        if (tied.length > 1 && tied.includes(buttonTrack)) recommendedTrack = buttonTrack;
    }

    return { recommendedTrack, ranking, sums, buttonTrack };
};
